import { cloneElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Col, FormLabel, Row } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ComposedChart,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import MenuConfig from "../../../../../constants/MenuConfig";
import { ThemeContext } from "../../../../../context/ThemeContext";
import { useBranch } from "../../../../../context/BranchContext";
import { endYear, formatMonthYearLabel, startYear } from "../../../../../utils/DateUtils";
import { ApiError } from "../../../../components/Common/ApiError";
import LoadingCenter from "../../../../components/Common/LoadingCenter";
import MonthSelect from "../../../../components/Common/MonthSelect";
import YearSelect from "../../../../components/Common/YearSelect";
import PdfExportButton from "../../../../components/Common/Report/PdfExportButton";
import CancelButton from "../../../../components/Forms/Element/CancelButton";
import { PerformancePanel, PerformanceSummaryCard, PerformanceWorkspace } from "../common/PerformanceWorkspace";
import EmployeeAvatar from "../common/EmployeeAvatar";
import CategoryTicketComparison from "./CategoryTicketComparison";
import {
    describeNeutralIndicator,
    describeReturnRate,
    formatCompactCurrency,
    formatCurrency,
    formatIndicatorValue,
    formatInteger,
    formatPercent,
    PERCENT_INDICATOR_TYPES
} from "../common/performanceFormat";
import "./EmployeePerformanceAnalysis.css";

// Cores dos gráficos. A linha e as barras de série única seguem o laranja da marca, como o
// painel principal; as três parcelas do faturamento usam a paleta categórica validada para os
// dois temas (sobre #ffffff e #1b1b1e), na ordem fixa avulsos, mensalidades, produtos.
const CHART_COLORS = {
    light: { line: "#d85d08", grid: "#eee5dc", axis: "#65656d", target: "#988c81", services: "#eb6834", subscription: "#2a78d6", products: "#1baf7a" },
    dark: { line: "#fc8019", grid: "rgba(242, 238, 234, 0.12)", axis: "#aaa3a0", target: "#aaa3a0", services: "#d95926", subscription: "#3987e5", products: "#199e70" }
};

// Largura dos gráficos no PDF: a folha A4 em retrato, descontadas as margens de 12mm.
const PRINT_CHART_WIDTH = 680;

const WEEKDAYS = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
const SHORT_MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

// O backend devolve LocalDate como "2026-10-08"; o formato em lista fica aceito por segurança.
const parseDate = (value) => {
    if (Array.isArray(value)) {
        return new Date(value[0], value[1] - 1, value[2]);
    }
    const [year, month, day] = String(value).split("-").map(Number);
    return new Date(year, month - 1, day);
};

const firstName = (name) => (name || "").split(" ")[0];
const formatBlockedTime = (minutes) => {
    const hours = Math.floor(Number(minutes || 0) / 60);
    const remainder = Number(minutes || 0) % 60;
    return remainder ? `${hours} h ${remainder} min` : `${hours} h`;
};

// Explicação curta abaixo da projeção de cada indicador, conforme a origem informada pelo backend.
// O ritmo (PACE) dispensa nota: é a regra geral, explicada no rodapé.
const PROJECTION_SOURCE_NOTES = {
    AVERAGE: "média atual mantida"
};

// Meta sem base suficiente (taxa de retorno com poucos clientes) não conta para o nível, então
// também não pede atenção.
const needsAttention = (indicator) => indicator.nextTarget != null && !indicator.insufficientBase
    && (indicator.missing != null || indicator.projected == null);

const EmployeePerformanceAnalysis = () => {
    const { employeeId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { selectedBranch } = useBranch();
    const { appTheme } = useContext(ThemeContext);
    const colors = appTheme === "dark" ? CHART_COLORS.dark : CHART_COLORS.light;

    const today = new Date();
    const month = Number(searchParams.get("month")) || today.getMonth() + 1;
    const year = Number(searchParams.get("year")) || today.getFullYear();
    // A filial do cabeçalho manda; sem ela, vale a que veio do painel pela URL.
    const branchId = selectedBranch?.id || searchParams.get("branchId") || undefined;

    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [printing, setPrinting] = useState(false);
    const printRef = useRef(null);

    useEffect(() => {
        setLoading(true);
        setAnalysis(null);
        axiosInstance
            .get(`${Api.performance.dashboard}/employees/${employeeId}/analysis`, {
                params: { year, month, branchId }
            })
            .then((resp) => setAnalysis(resp.data))
            .catch((err) => ApiError(err))
            .finally(() => setLoading(false));
    }, [employeeId, year, month, branchId]);

    const changePeriod = (nextMonth, nextYear) => {
        const params = new URLSearchParams(searchParams);
        params.set("month", nextMonth);
        params.set("year", nextYear);
        setSearchParams(params);
    };

    // O gráfico do Recharts não se ajusta sozinho depois de copiado para o documento de
    // impressão; antes da cópia a página se redesenha com a largura da folha e sem animação.
    const preparePrint = useCallback(() => new Promise((resolve) => {
        setPrinting(true);
        setTimeout(resolve, 300);
    }), []);

    const inProgress = analysis?.periodStatus === "IN_PROGRESS";
    // Ganhos (cartão, simulação por nível e coluna do histórico) só para quem tem o módulo
    // Relatórios; sem ele o backend já manda os valores zerados, e a tela esconde as partes.
    const earningsVisible = Boolean(analysis?.earningsVisible);
    const kpiColumns = earningsVisible ? 3 : 4;
    const monthLabel = formatMonthYearLabel(month, year);
    const name = firstName(analysis?.employeeName);
    const appointmentGrowth = analysis?.appointmentGrowth;
    const previousComparisonDate = appointmentGrowth ? parseDate(appointmentGrowth.previousReferenceDate) : null;
    // No mês em andamento a comparação vai até agora e o mês anterior até o mesmo número de dias de
    // funcionamento, no mesmo horário (recorte do relatório de crescimento), por isso usa a contagem
    // própria do backend.
    const currentComparisonDate = appointmentGrowth ? parseDate(appointmentGrowth.currentReferenceDate) : null;
    const comparedAppointments = appointmentGrowth ? Number(appointmentGrowth.currentAppointments) : 0;
    const previousComparisonLabel = previousComparisonDate
        ? formatMonthYearLabel(previousComparisonDate.getMonth() + 1, previousComparisonDate.getFullYear())
        : null;
    const appointmentDifference = appointmentGrowth
        ? comparedAppointments - Number(appointmentGrowth.previousAppointments)
        : 0;
    // As barras usam a maior contagem como escala; o mínimo de 1 evita divisão por zero
    // quando nenhum dos dois meses teve atendimentos.
    const appointmentBarMaximum = appointmentGrowth
        ? Math.max(comparedAppointments, Number(appointmentGrowth.previousAppointments), 1)
        : 1;
    const differenceAmount = formatInteger(Math.abs(appointmentDifference));
    const differenceUnit = Math.abs(appointmentDifference) === 1 ? "atendimento" : "atendimentos";

    // Só a meta de faturamento do próximo nível (o primeiro que a projeção ainda não atinge);
    // com todos atingidos, a do último. As quatro metas juntas esticavam o eixo até R$ 20 mil e
    // achatavam a curva do começo do mês.
    const revenueTargets = useMemo(() => {
        const levels = analysis?.levels || [];
        const level = levels.find((item) => item.levelId === analysis?.nextLevel?.levelId) || levels[levels.length - 1];
        const goal = level?.indicators.find((indicator) => indicator.type === "REVENUE");
        return goal ? [{ name: level.levelName, value: Number(goal.targetValue) }] : [];
    }, [analysis]);

    const dailyData = useMemo(() => {
        if (!analysis) return [];
        const referenceDay = parseDate(analysis.referenceDate).getDate();
        const remainingWorkdays = analysis.projection.workingDaysRemaining;
        const remainingCalendarDays = analysis.projection.calendarDaysTotal - analysis.projection.calendarDaysElapsed;
        let elapsedWorkdays = 0;

        return analysis.daily.map((point) => {
            const date = parseDate(point.date);
            const future = point.actual == null && point.projected != null;
            if (future && point.workingDay) elapsedWorkdays += 1;

            // Compatibilidade com o servidor anterior ao detalhamento diário: usa os mesmos
            // totais mensais e fatores que já alimentam os cartões. O valor diário vindo da
            // API sempre tem prioridade quando estiver disponível.
            let earnings = point.actual != null ? point.realizedEarnings : point.projectedEarnings;
            if (!earnings && future && analysis.earningsVisible) {
                const workingShare = remainingWorkdays > 0 ? elapsedWorkdays / remainingWorkdays : 0;
                const calendarShare = remainingCalendarDays > 0
                    ? (date.getDate() - referenceDay) / remainingCalendarDays : 0;
                const estimate = (key, share) => analysis.actual[key]
                    + (analysis.projected[key] - analysis.actual[key]) * share;
                earnings = {
                    services: estimate("commissionServices", workingShare),
                    products: estimate("commissionProducts", workingShare),
                    subscriptions: estimate("subscriptionEarnings", calendarShare),
                    potBonus: estimate("potBonus", calendarShare),
                    bonuses: analysis.projected.bonuses
                };
            }
            return {
                day: date.getDate(),
                weekday: WEEKDAYS[date.getDay()],
                workingDay: point.workingDay,
                actual: point.actual,
                projected: point.projected,
                earnings
            };
        });
    }, [analysis]);

    const historyData = useMemo(() => (analysis?.history || []).map((item, index, list) => ({
        ...item,
        label: `${SHORT_MONTHS[item.month - 1]}/${String(item.year).slice(2)}${index === list.length - 1 && inProgress ? "*" : ""}`
    })), [analysis, inProgress]);

    // As metas obrigatórias pendentes determinam o próximo nível. Mostrá-las primeiro evita
    // que indicadores já cumpridos escondam as ações necessárias para alcançá-lo.
    const sortedIndicators = useMemo(() => [...(analysis?.indicators || [])].sort((a, b) => {
        const priority = (indicator) => indicator.required && needsAttention(indicator) ? 0
            : indicator.required ? 1 : needsAttention(indicator) ? 2 : 3;
        return priority(a) - priority(b);
    }), [analysis]);
    const requiredGaps = sortedIndicators.filter((indicator) => indicator.required && needsAttention(indicator));

    // Os gráficos usam a largura do contêiner na tela e uma largura fixa no PDF.
    const renderChart = (height, chart) => (printing
        ? cloneElement(chart, { width: PRINT_CHART_WIDTH, height })
        : <ResponsiveContainer width="100%" height={height}>{chart}</ResponsiveContainer>);

    const axisTick = { fill: colors.axis, fontSize: 12 };

    const renderHeadline = () => {
        if (!analysis) return null;
        const projectedName = analysis.projectedLevel?.name;
        const currentName = analysis.currentLevel?.name;
        if (inProgress) {
            return (
                <>
                    Se continuar neste ritmo, {name} fecha {monthLabel.toLowerCase().replace(/ de \d+$/, "")}{" "}
                    {projectedName ? <>no nível <strong>{projectedName}</strong></> : <>sem atingir nenhum nível</>}
                    , com <strong>{formatCurrency(analysis.projected.totalProduction)}</strong> de faturamento gerado.
                </>
            );
        }
        return (
            <>
                Em {monthLabel.toLowerCase()}, {name} fechou{" "}
                {currentName ? <>no nível <strong>{currentName}</strong></> : <>sem atingir nenhum nível</>}
                , com <strong>{formatCurrency(analysis.actual.totalProduction)}</strong> de faturamento gerado.
            </>
        );
    };

    // Situação de um indicador frente à meta do próximo nível: no ritmo, ou quanto falta.
    const renderGap = (indicator) => {
        if (indicator.nextTarget == null) {
            return <span className="text-muted">Sem meta</span>;
        }
        if (indicator.insufficientBase) {
            return (
                <span className="employee-analysis__status employee-analysis__status--optional">
                    <i className="fas fa-circle-info" aria-hidden="true"></i> {describeNeutralIndicator(indicator)}: não conta para o nível
                </span>
            );
        }
        if (indicator.projected == null) {
            return <span className="employee-analysis__status employee-analysis__status--warn">Sem dados para avaliar</span>;
        }
        if (indicator.missing == null) {
            return (
                <span className="employee-analysis__status employee-analysis__status--good">
                    <i className="fas fa-circle-check" aria-hidden="true"></i> {inProgress ? "Projeção atinge a meta" : "Meta atingida"}
                </span>
            );
        }
        let text;
        if (PERCENT_INDICATOR_TYPES.includes(indicator.type)) {
            text = `Aumentar ${formatPercent(indicator.missing).replace("%", "")} pontos percentuais`;
        } else if (indicator.keepsRatio) {
            text = `Aumentar ${formatIndicatorValue(indicator, indicator.missing)} por atendimento`;
        } else {
            text = inProgress
                ? `Na projeção, faltam ${formatIndicatorValue(indicator, indicator.missing)}`
                : `Faltaram ${formatIndicatorValue(indicator, indicator.missing)} para a meta`;
        }
        return (
            <span className={`employee-analysis__status ${indicator.required ? "employee-analysis__status--warn" : "employee-analysis__status--optional"}`}>
                <i className={`fas ${indicator.required ? "fa-triangle-exclamation" : "fa-circle-info"}`} aria-hidden="true"></i> {text}
            </span>
        );
    };

    const DailyTooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const point = payload[0].payload;
        const value = point.actual ?? point.projected;
        const earnings = point.earnings;
        const totalEarnings = earnings
            ? earnings.services + earnings.products + earnings.subscriptions + earnings.potBonus + earnings.bonuses
            : null;
        return (
            <div className="employee-analysis__tooltip">
                <strong>Dia {point.day} ({point.weekday})</strong>
                <span>{point.actual != null ? "Faturamento até o dia" : "Faturamento previsto até o dia"}: {formatCurrency(value)}</span>
                {earnings && (
                    <div className="employee-analysis__tooltip-breakdown">
                        <strong>Ganhos {point.actual != null ? "acumulados" : "previstos"} até o dia: {formatCurrency(totalEarnings)}</strong>
                        <span>Serviços avulsos: {formatCurrency(earnings.services)}</span>
                        <span>Produtos: {formatCurrency(earnings.products)}</span>
                        <span>Assinaturas: {formatCurrency(earnings.subscriptions)}</span>
                        {earnings.potBonus !== 0 && <span>Adicional do Pote: {formatCurrency(earnings.potBonus)}</span>}
                        {earnings.bonuses !== 0 && <span>Bonificação: {formatCurrency(earnings.bonuses)}</span>}
                    </div>
                )}
                {!point.workingDay && <small>Filial fechada neste dia</small>}
            </div>
        );
    };

    const HistoryTooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const item = payload[0].payload;
        return (
            <div className="employee-analysis__tooltip">
                <strong>{formatMonthYearLabel(item.month, item.year)}</strong>
                <span>Faturamento gerado: {formatCurrency(item.totalProduction)}</span>
                {earningsVisible && <span>Ganhos: {formatCurrency(item.totalEarnings)}</span>}
                <span>Atendimentos: {formatInteger(item.appointments)}</span>
                <span>Nível: {item.levelName || "Sem nível"}</span>
            </div>
        );
    };

    const composition = analysis ? [
        { key: "services", label: "Serviços avulsos", value: analysis.actual.directServices, color: colors.services },
        { key: "subscription", label: "Parte das mensalidades", value: analysis.actual.subscriptionProduction, color: colors.subscription },
        { key: "products", label: "Produtos", value: analysis.actual.products, color: colors.products }
    ] : [];
    const compositionTotal = composition.reduce((sum, item) => sum + item.value, 0);

    const simulationRows = analysis?.simulation || [];
    const simulationValue = (row) => (inProgress ? row.projectedTotalEarnings : row.totalEarnings);
    const currentRuleEarnings = analysis ? (inProgress ? analysis.projected.totalEarnings : analysis.actual.totalEarnings) : 0;
    const maxSimulation = Math.max(currentRuleEarnings, ...simulationRows.map(simulationValue), 1);
    const exampleLevel = simulationRows[0];
    const exampleDifference = exampleLevel ? simulationValue(exampleLevel) - currentRuleEarnings : 0;

    const projectionDaysNote = analysis && inProgress
        ? analysis.projection.basis === "WORKING_DAYS"
            ? `A filial abriu em ${analysis.projection.workingDaysElapsed} dias até agora e deve abrir em ${analysis.projection.workingDaysTotal} dias no mês.`
            : `Já passaram ${analysis.projection.workingDaysElapsed} dos ${analysis.projection.workingDaysTotal} dias do mês.`
        : null;
    const projectionNote = projectionDaysNote && `${projectionDaysNote} A previsão estima o faturamento se o ritmo continuar.`;

    return (
        <PerformanceWorkspace
            eyebrow="Desempenho"
            title="Análise individual"
            subtitle="Resultado do mês, projeção do fechamento e o que falta para o próximo nível."
        >
            <PerformancePanel className="mb-4 d-print-none">
                <Row className="align-items-end g-3">
                    <Col xs={12} sm={6} lg={3}>
                        <FormLabel className="small fw-bold">Mês</FormLabel>
                        <MonthSelect
                            value={{ value: month, label: month }}
                            onChange={(option) => option && changePeriod(option.value, year)}
                        />
                    </Col>
                    <Col xs={12} sm={6} lg={3}>
                        <FormLabel className="small fw-bold">Ano</FormLabel>
                        <YearSelect
                            value={{ value: year, label: year }}
                            onChange={(option) => option && changePeriod(month, option.value)}
                            startYear={startYear()}
                            endYear={endYear()}
                        />
                    </Col>
                    <Col xs={12} sm={6} lg={3}>
                        {/* Botão secundário padrão do sistema (fundo claro), o mesmo do Cancelar dos formulários. */}
                        <CancelButton className="w-100" onClick={() => navigate(MenuConfig.performance.dashboard.route)}>
                            <i className="fas fa-arrow-left me-2"></i>
                            Voltar ao painel
                        </CancelButton>
                    </Col>
                    <Col xs={12} sm={6} lg={3}>
                        <PdfExportButton
                            contentRef={printRef}
                            documentTitle={`analise-${analysis?.employeeName || "colaborador"}-${String(month).padStart(2, "0")}-${year}`}
                            variant="primary"
                            className="w-100"
                            disabled={!analysis || loading}
                            pageStyle="@page { size: A4 portrait; margin: 12mm; }"
                            onBeforeGetContent={preparePrint}
                            onAfterPrint={() => setPrinting(false)}
                        >
                            <i className="fas fa-file-pdf me-2"></i>
                            Gerar PDF
                        </PdfExportButton>
                    </Col>
                </Row>
            </PerformancePanel>

            {loading && <LoadingCenter />}

            {!loading && analysis && (
                <div ref={printRef} className="employee-analysis">
                    {/* Identificação com a foto do cadastro (ou as iniciais), na tela e no PDF: a
                        folha entregue ao colaborador precisa dizer de quem é e de que mês. */}
                    <div className="employee-analysis__profile">
                        <div className="employee-analysis__avatar">
                            <EmployeeAvatar employeeId={analysis.employeeId} name={analysis.employeeName} />
                        </div>
                        <div className="employee-analysis__profile-text">
                            <h4>{analysis.employeeName}</h4>
                            <div className="employee-analysis__profile-meta">
                                <span className="employee-analysis__period">{monthLabel}</span>
                                <span className="employee-analysis__plan">{analysis.planName}</span>
                            </div>
                            <div className="employee-analysis__period-status">
                                <strong>{inProgress ? "Mês em andamento" : "Mês fechado"}</strong>
                                {inProgress && (
                                    <span>Resultados até {parseDate(analysis.referenceDate).toLocaleDateString("pt-BR")}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <Row className="g-3 mb-4 employee-analysis__kpis">
                        <Col xl={kpiColumns} md={6}>
                            <PerformanceSummaryCard
                                label="Faturamento gerado"
                                value={formatCurrency(analysis.actual.totalProduction)}
                                detail={inProgress ? `Projeção do mês: ${formatCurrency(analysis.projected.totalProduction)}` : "Total do mês, o mesmo da tela Comissões"}
                                icon="fa-money-bill-trend-up"
                                tone="sky"
                            />
                        </Col>
                        {earningsVisible && (
                            <Col xl={kpiColumns} md={6}>
                                <PerformanceSummaryCard
                                    label="Ganhos no mês"
                                    value={formatCurrency(analysis.actual.totalEarnings)}
                                    detail={inProgress ? `Projeção do mês: ${formatCurrency(analysis.projected.totalEarnings)}` : "Comissões, Pote e bonificações"}
                                    icon="fa-wallet"
                                    tone="green"
                                />
                            </Col>
                        )}
                        <Col xl={kpiColumns} md={6}>
                            <PerformanceSummaryCard
                                label="Atendimentos realizados"
                                value={formatInteger(analysis.appointments)}
                                detail={(
                                    <>
                                        <span className="employee-analysis__appointment-detail">
                                            <span>Clientes diferentes</span>
                                            <b>{formatInteger(analysis.customers)}</b>
                                        </span>
                                        {inProgress && (
                                            <>
                                                <span className="employee-analysis__appointment-detail">
                                                    <span>Previsão de atendimentos</span>
                                                    <b>{formatInteger(analysis.projectedAppointments)}</b>
                                                </span>
                                                <small className="employee-analysis__appointment-note">Se mantiver o ritmo até o fim do mês</small>
                                            </>
                                        )}
                                    </>
                                )}
                                icon="fa-calendar-check"
                                tone="amber"
                            />
                        </Col>
                        <Col xl={kpiColumns} md={6}>
                            <PerformanceSummaryCard
                                label="Nível"
                                value={analysis.currentLevel?.name || "Sem nível"}
                                detail={inProgress
                                    ? `Se continuar assim: ${analysis.projectedLevel?.name || "sem nível"}`
                                    : analysis.currentLevel ? `Comissão do nível: ${formatPercent(analysis.currentLevel.commissionPercent)}` : "Nenhum nível atingido no mês"}
                                icon="fa-ranking-star"
                                tone="coral"
                            />
                        </Col>
                    </Row>

                    {analysis.activity && (
                        <PerformancePanel title="Agenda e retorno" className="mb-4 employee-analysis__section">
                            <div className="employee-analysis__activity-grid">
                                <div className="employee-analysis__activity-item">
                                    <span>Horas bloqueadas na agenda</span>
                                    <strong>{formatBlockedTime(analysis.activity.blockedMinutes)}</strong>
                                    <small>{inProgress ? "Bloqueios até hoje" : "Bloqueios do mês"}</small>
                                </div>
                                <div className="employee-analysis__activity-item">
                                    <span>Dias trabalhados</span>
                                    <strong>{analysis.activity.scheduledDays == null ? "—" : formatInteger(analysis.activity.scheduledDays)}</strong>
                                    <small>{analysis.activity.scheduledDays == null ? "Sem horário cadastrado na filial" : "Filial aberta, escalado e sem folga"}</small>
                                </div>
                                <div className="employee-analysis__activity-item">
                                    <span>Taxa de retorno</span>
                                    {/* A conta vem pronta do backend (ReturnRateService): avulsos cujo prazo
                                        de 60 dias venceu no mês e quantos voltaram. */}
                                    <strong>{analysis.activity.returnRate?.rate == null ? "—" : formatPercent(analysis.activity.returnRate.rate)}</strong>
                                    <small>{describeReturnRate(analysis.activity.returnRate)}</small>
                                </div>
                            </div>
                        </PerformancePanel>
                    )}

                    {appointmentGrowth && (
                        <PerformancePanel
                            title="Atendimentos comparados com o mês anterior"
                            subtitle={inProgress
                                ? "Mês em andamento: os dois meses têm o mesmo número de dias de funcionamento, e o dia de hoje vai até agora, contra o dia correspondente até o mesmo horário."
                                : "A comparação considera o total de cada mês."}
                            className="mb-4 employee-analysis__section"
                        >
                            <div className="employee-analysis__growth-chart">
                                <div className="employee-analysis__growth-row">
                                    <div className="employee-analysis__growth-label">
                                        <span>{inProgress ? `De 1º a ${currentComparisonDate.getDate()} de ${monthLabel}` : monthLabel}</span>
                                        <strong>{formatInteger(comparedAppointments)} {comparedAppointments === 1 ? "atendimento" : "atendimentos"}</strong>
                                    </div>
                                    <div className="employee-analysis__growth-track" aria-hidden="true">
                                        <div className="employee-analysis__growth-bar employee-analysis__growth-bar--current" style={{ width: `${comparedAppointments / appointmentBarMaximum * 100}%`, backgroundColor: colors.line }} />
                                    </div>
                                </div>
                                <div className="employee-analysis__growth-row">
                                    <div className="employee-analysis__growth-label">
                                        <span>{inProgress ? `De 1º a ${previousComparisonDate.getDate()} de ${previousComparisonLabel}` : previousComparisonLabel}</span>
                                        <strong>{formatInteger(appointmentGrowth.previousAppointments)} {Number(appointmentGrowth.previousAppointments) === 1 ? "atendimento" : "atendimentos"}</strong>
                                    </div>
                                    <div className="employee-analysis__growth-track" aria-hidden="true">
                                        <div className="employee-analysis__growth-bar" style={{ width: `${Number(appointmentGrowth.previousAppointments) / appointmentBarMaximum * 100}%`, backgroundColor: colors.subscription }} />
                                    </div>
                                </div>
                            </div>
                            {/* A diferença em quantidade vem antes do percentual para a comparação ser compreensível
                                mesmo quando o mês anterior teve zero atendimentos. */}
                            <p className={`employee-analysis__growth-result ${appointmentDifference > 0 ? "employee-analysis__diff--up" : appointmentDifference < 0 ? "employee-analysis__diff--down" : ""}`}>
                                {appointmentDifference > 0
                                    ? `${differenceAmount} ${differenceUnit} a mais que no mês anterior`
                                    : appointmentDifference < 0
                                        ? `${differenceAmount} ${differenceUnit} a menos que no mês anterior`
                                        : "Mesmo número de atendimentos nos dois meses"}
                                {appointmentDifference !== 0 && appointmentGrowth.percentageChange !== null
                                    && ` (${appointmentDifference > 0 ? "aumento" : "queda"} de ${formatPercent(Math.abs(appointmentGrowth.percentageChange))})`}.
                            </p>
                            {appointmentGrowth.percentageChange === null && (
                                <small className="text-muted">Não há porcentagem para comparar porque o mês anterior teve zero atendimentos.</small>
                            )}
                        </PerformancePanel>
                    )}

                    {/* Detalhe do quadro da equipe no painel: o ticket deste colaborador ao lado da equipe.
                        Sem atendimento de ninguém no mês a lista vem vazia e a seção some. */}
                    {analysis.categoryTickets?.length > 0 && (
                        <CategoryTicketComparison groups={analysis.categoryTickets} employeeName={name} colors={colors} />
                    )}

                    <PerformancePanel
                        title="Evolução do faturamento no mês"
                        subtitle={inProgress
                            ? `Faturamento acumulado por dia. A linha tracejada mostra a previsão; a linha fina mostra a meta do próximo nível.${earningsVisible ? " Passe o mouse ou toque na linha para ver os ganhos realizados e previstos." : ""}`
                            : "Faturamento gerado acumulado dia a dia; a linha fina é a meta de faturamento do próximo nível."}
                        className="mb-4 employee-analysis__section"
                    >
                        <div className="employee-analysis__legend" aria-hidden="true">
                            <span><i className="employee-analysis__key employee-analysis__key--solid" style={{ borderColor: colors.line }}></i> Realizado</span>
                            {inProgress && <span><i className="employee-analysis__key employee-analysis__key--dashed" style={{ borderColor: colors.line }}></i> Projeção</span>}
                            {revenueTargets.length > 0 && <span><i className="employee-analysis__key employee-analysis__key--target" style={{ borderColor: colors.target }}></i> Meta do nível {revenueTargets[0].name}</span>}
                        </div>
                        {renderChart(320, (
                            <ComposedChart data={dailyData} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
                                <CartesianGrid stroke={colors.grid} vertical={false} />
                                <XAxis dataKey="day" tick={axisTick} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
                                <YAxis
                                    tick={axisTick}
                                    axisLine={false}
                                    tickLine={false}
                                    width={78}
                                    tickFormatter={formatCompactCurrency}
                                    domain={[0, (dataMax) => Math.max(dataMax, ...revenueTargets.map((target) => target.value)) * 1.05]}
                                />
                                {!printing && <Tooltip content={<DailyTooltip />} />}
                                {revenueTargets.map((target) => (
                                    <ReferenceLine
                                        key={target.name}
                                        y={target.value}
                                        stroke={colors.target}
                                        strokeWidth={1}
                                        label={{ value: `${target.name} ${formatCompactCurrency(target.value)}`, position: "insideTopRight", fill: colors.axis, fontSize: 11 }}
                                    />
                                ))}
                                <Line type="monotone" dataKey="actual" name="Realizado" stroke={colors.line} strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls={false} isAnimationActive={!printing} />
                                {inProgress && (
                                    <Line type="monotone" dataKey="projected" name="Projeção" stroke={colors.line} strokeWidth={2} strokeDasharray="6 5" dot={false} activeDot={{ r: 4 }} connectNulls={false} isAnimationActive={!printing} />
                                )}
                            </ComposedChart>
                        ))}
                        <div className="employee-analysis__chart-summary">
                            <div className="employee-analysis__chart-summary-values">
                                <div className="employee-analysis__chart-summary-value">
                                    <span>{inProgress ? "Faturamento até hoje" : "Faturamento no mês"}</span>
                                    <strong>{formatCurrency(analysis.actual.totalProduction)}</strong>
                                </div>
                                {inProgress && (
                                    <div className="employee-analysis__chart-summary-value">
                                        <span>Previsão para o fim do mês</span>
                                        <strong>{formatCurrency(analysis.projected.totalProduction)}</strong>
                                    </div>
                                )}
                            </div>
                            {projectionNote && <p className="employee-analysis__chart-summary-note">{projectionNote}</p>}
                        </div>
                    </PerformancePanel>

                    <PerformancePanel
                        title={inProgress ? "Se continuar assim" : "Como fechou o mês"}
                        className="mb-4 employee-analysis__section"
                    >
                        <p className="employee-analysis__headline">{renderHeadline()}</p>
                        {analysis.nextLevel && (
                            <div className="employee-analysis__goal-summary" role="status">
                                <strong>{requiredGaps.length === 0
                                    ? inProgress ? "Todas as metas obrigatórias estão no ritmo" : "Todas as metas obrigatórias foram atingidas"
                                    : `${requiredGaps.length} ${requiredGaps.length === 1 ? "meta obrigatória precisa" : "metas obrigatórias precisam"} de atenção`}</strong>
                <span>{requiredGaps.length === 0
                                    ? "Confira abaixo os indicadores opcionais e o resultado projetado."
                                    : `Para chegar ao nível ${analysis.nextLevel.name}, revise ${requiredGaps.map((indicator) => indicator.name).join(", ")}.`}</span>
                            </div>
                        )}
                        <div className="table-responsive">
                            <table className="table align-middle employee-analysis__table">
                                <thead>
                                    <tr>
                                        <th scope="col">Indicador</th>
                                        <th scope="col" className="text-end">{inProgress ? "Até hoje" : "Resultado"}</th>
                                        {inProgress && <th scope="col" className="text-end">Projeção</th>}
                                        <th scope="col" className="text-end">{analysis.nextLevel ? `Meta ${analysis.nextLevel.name}` : "Meta"}</th>
                                        <th scope="col">Situação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedIndicators.map((indicator) => (
                                        <tr key={indicator.indicatorId} className={indicator.required && needsAttention(indicator) ? "employee-analysis__row--attention" : undefined}>
                                            <th scope="row">
                                                {indicator.name}
                                                {!indicator.required && <small className="text-muted ms-1">(opcional)</small>}
                                            </th>
                                            <td className="text-end">{indicator.actual == null ? "-" : formatIndicatorValue(indicator, indicator.actual)}</td>
                                            {inProgress && (
                                                <td className="text-end">
                                                    {indicator.projected == null ? "-" : formatIndicatorValue(indicator, indicator.projected)}
                                                    {indicator.projected != null && PROJECTION_SOURCE_NOTES[indicator.projectionSource] && (
                                                        <small className="d-block text-muted">{PROJECTION_SOURCE_NOTES[indicator.projectionSource]}</small>
                                                    )}
                                                </td>
                                            )}
                                            <td className="text-end">{indicator.nextTarget == null ? "-" : formatIndicatorValue(indicator, indicator.nextTarget)}</td>
                                            <td>{renderGap(indicator)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {!analysis.nextLevel && (
                            <p className="text-muted small mb-0">{inProgress ? "No ritmo atual, todos os níveis do plano são atingidos." : "Todos os níveis do plano foram atingidos."}</p>
                        )}
                    </PerformancePanel>

                    {earningsVisible && (
                    <PerformancePanel
                        title="Quanto receberia em cada nível"
                        subtitle={`Simulação com ${inProgress ? "o faturamento projetado" : "o faturamento do mês"}. Só muda a taxa de comissão dos serviços avulsos; os demais ganhos ficam iguais. Nenhuma comissão é alterada.`}
                        className="mb-4 employee-analysis__section"
                    >
                        <p className="employee-analysis__simulation-note">
                            <strong>Regra atual:</strong> {formatCurrency(currentRuleEarnings)}.
                            {exampleLevel && <>{" "}Com a taxa do nível {exampleLevel.name}, seria {formatCurrency(simulationValue(exampleLevel))}{Math.abs(exampleDifference) < 0.005
                                ? ", o mesmo total."
                                : `, ${formatCurrency(Math.abs(exampleDifference))} ${exampleDifference > 0 ? "a mais" : "a menos"}.`}</>}
                        </p>
                        <div className="table-responsive">
                            <table className="table align-middle employee-analysis__table">
                                <thead>
                                    <tr>
                                        <th>Nível</th>
                                        <th className="text-end">Comissão dos serviços</th>
                                        <th>{inProgress ? "Total no fim do mês" : "Total do mês"}</th>
                                        <th className="text-end">Diferença vs. regra atual</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="employee-analysis__row--current">
                                        <td><strong>Regra atual</strong></td>
                                        <td className="text-end">{analysis.currentServicePercent != null ? formatPercent(analysis.currentServicePercent) : "-"}</td>
                                        <td>
                                            <div className="employee-analysis__bar-cell">
                                                <span className="employee-analysis__bar" style={{ width: `${(currentRuleEarnings / maxSimulation) * 100}%`, background: colors.target }}></span>
                                                <strong>{formatCurrency(currentRuleEarnings)}</strong>
                                            </div>
                                        </td>
                                        <td className="text-end">-</td>
                                    </tr>
                                    {simulationRows.map((row) => {
                                        const value = simulationValue(row);
                                        const difference = inProgress ? row.projectedDifference : row.totalEarnings - analysis.actual.totalEarnings;
                                        const reached = inProgress ? row.projectedReached : row.reached;
                                        return (
                                            <tr key={row.levelId}>
                                                <td>
                                                    <span className="employee-analysis__level-dot" style={{ background: row.color || colors.target }}></span>
                                                    {row.name}
                                                    {reached && (
                                                        <span className="employee-analysis__status employee-analysis__status--good ms-2">
                                                            <i className="fas fa-circle-check" aria-hidden="true"></i> {inProgress ? "no ritmo" : "atingido"}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-end">{formatPercent(row.commissionPercent)}</td>
                                                <td>
                                                    <div className="employee-analysis__bar-cell">
                                                        <span className="employee-analysis__bar" style={{ width: `${(value / maxSimulation) * 100}%`, background: colors.line }}></span>
                                                        <strong>{formatCurrency(value)}</strong>
                                                    </div>
                                                </td>
                                                <td className="text-end">
                                                    <span className={difference > 0.005 ? "employee-analysis__diff--up" : difference < -0.005 ? "employee-analysis__diff--down" : undefined}>
                                                        {difference > 0.005 ? "+ " : difference < -0.005 ? "- " : ""}{formatCurrency(Math.abs(difference))}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </PerformancePanel>
                    )}

                    <Row className="g-4 employee-analysis__bottom-row">
                        <Col xl={5}>
                            <PerformancePanel
                                title="Composição do faturamento"
                                subtitle={inProgress ? "De onde vem o faturamento gerado até hoje." : "De onde veio o faturamento gerado no mês."}
                                className="h-100 employee-analysis__section"
                            >
                                {compositionTotal > 0 ? (
                                    <>
                                        <div className="employee-analysis__stack" role="img" aria-label="Composição do faturamento">
                                            {composition.filter((item) => item.value > 0).map((item) => (
                                                <span key={item.key} style={{ width: `${(item.value / compositionTotal) * 100}%`, background: item.color }}></span>
                                            ))}
                                        </div>
                                        <ul className="employee-analysis__stack-legend">
                                            {composition.map((item) => (
                                                <li key={item.key}>
                                                    <i style={{ background: item.color }} aria-hidden="true"></i>
                                                    <span>{item.label}</span>
                                                    <strong>{formatCurrency(item.value)}</strong>
                                                    <small>{formatPercent((item.value / compositionTotal) * 100)}</small>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                ) : (
                                    <p className="text-muted mb-0">Ainda não há faturamento no mês.</p>
                                )}
                            </PerformancePanel>
                        </Col>
                        <Col xl={7}>
                            <PerformancePanel
                                title="Últimos 6 meses"
                                subtitle={`Faturamento gerado por mês${inProgress ? "; * mês em andamento, até hoje" : ""}.`}
                                className="h-100 employee-analysis__section"
                            >
                                {renderChart(220, (
                                    <BarChart data={historyData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                        <CartesianGrid stroke={colors.grid} vertical={false} />
                                        <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} />
                                        <YAxis tick={axisTick} axisLine={false} tickLine={false} width={78} tickFormatter={formatCompactCurrency} />
                                        {!printing && <Tooltip content={<HistoryTooltip />} cursor={{ fill: colors.grid }} />}
                                        <Bar dataKey="totalProduction" name="Faturamento gerado" fill={colors.line} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={!printing} />
                                    </BarChart>
                                ))}
                                <div className="table-responsive mt-2">
                                    <table className="table table-sm align-middle employee-analysis__table mb-0">
                                        <thead>
                                            <tr>
                                                <th>Mês</th>
                                                <th className="text-end">Faturamento</th>
                                                {earningsVisible && <th className="text-end">Ganhos</th>}
                                                <th className="text-end">Atend.</th>
                                                <th>Nível</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historyData.map((item) => (
                                                <tr key={`${item.year}-${item.month}`}>
                                                    <td>{item.label}</td>
                                                    <td className="text-end">{formatCurrency(item.totalProduction)}</td>
                                                    {earningsVisible && <td className="text-end">{formatCurrency(item.totalEarnings)}</td>}
                                                    <td className="text-end">{formatInteger(item.appointments)}</td>
                                                    <td>{item.levelName || "Sem nível"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </PerformancePanel>
                        </Col>
                    </Row>

                    {/* Referências para conferir os totais e regras diferentes de projeção sem
                        misturar a origem dos dados com o cálculo do mês em andamento. */}
                    <div className="employee-analysis__footnote">
                        <strong>Como ler os números</strong>
                        <ul>
                            <li>
                                <strong>Totais:</strong> faturamento{earningsVisible ? " e ganhos são os mesmos valores" : " é o mesmo valor"} da tela Comissões
                                {earningsVisible ? " (\"Faturamento: total\" e \"Ganhos: total\")" : " (\"Faturamento: total\")"} e do cartão do colaborador em Desempenho, para o mesmo mês e filial.
                            </li>
                            <li><strong>Atendimentos:</strong> quantidade de comandas, como no indicador "Qtd. atendimentos".</li>
                            {inProgress && (
                                <>
                                    <li><strong>Projeção:</strong> vendas em comandas seguem a média por dia em que a filial abre; a parte das mensalidades segue a média por dia corrido.</li>
                                    <li><strong>Tickets:</strong> mantêm a média atual.</li>
                                    <li><strong>Retorno:</strong> usa a taxa do mês anterior, pois a taxa deste mês ainda está em formação.</li>
                                </>
                            )}
                        </ul>
                    </div>
                </div>
            )}
        </PerformanceWorkspace>
    );
};

export default EmployeePerformanceAnalysis;
